import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { PropertyCard } from '../components/PropertyCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const propertyTypes = ['Appartamento', 'Villa', 'Mansarda', 'Terreno', 'Locale Commerciale', 'Ufficio', 'Box Auto', 'Posto Auto', 'Soluzione Semi-Indipendente', 'Soluzione Indipendente'];
const contractTypes = [
  { value: 'vendita', label: 'In Vendita' },
  { value: 'affitto', label: 'In Affitto' },
];

export default function PropertiesPage() {
  const [searchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    property_type: searchParams.get('type') || '',
    contract_type: searchParams.get('contract') || '',
    city: searchParams.get('city') || '',
    min_price: searchParams.get('min') || '',
    max_price: searchParams.get('max') || '',
    min_rooms: searchParams.get('rooms') || '',
  });
  const [sortBy, setSortBy] = useState('price_asc');

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.property_type && filters.property_type !== 'all') params.append('property_type', filters.property_type);
      if (filters.contract_type && filters.contract_type !== 'all') params.append('contract_type', filters.contract_type);
      if (filters.city) params.append('city', filters.city);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.min_rooms) params.append('min_rooms', filters.min_rooms);
      if (sortBy) params.append('sort', sortBy);

      const { data } = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProperties();
  };

  const clearFilters = () => {
    setFilters({
      property_type: '',
      contract_type: '',
      city: '',
      min_price: '',
      max_price: '',
      min_rooms: '',
    });
  };

  return (
    <div data-testid="properties-page">
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl animate-fadeIn">
            <p className="text-xs font-sans uppercase tracking-[0.2em] font-medium text-accent mb-4">
              I Nostri Annunci
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-6">
              Trova il tuo immobile
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed">
              Esplora la nostra selezione di immobili in vendita e in affitto a Caserta e provincia.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-border sticky top-20 z-40" data-testid="filters-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSearch}>
            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-4">
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <Input
                  placeholder="Cerca per città..."
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                  className="pl-10"
                  data-testid="filter-city"
                />
              </div>
              <Select
                value={filters.contract_type}
                onValueChange={(value) => setFilters({ ...filters, contract_type: value })}
              >
                <SelectTrigger className="w-[160px]" data-testid="filter-contract">
                  <SelectValue placeholder="Tipo contratto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {contractTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.property_type}
                onValueChange={(value) => setFilters({ ...filters, property_type: value })}
              >
                <SelectTrigger className="w-[180px]" data-testid="filter-property-type">
                  <SelectValue placeholder="Tipo immobile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  {propertyTypes.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                type="number"
                placeholder="Prezzo min"
                value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                className="w-[130px]"
                data-testid="filter-min-price"
              />
              <Input
                type="number"
                placeholder="Prezzo max"
                value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                className="w-[130px]"
                data-testid="filter-max-price"
              />
              <Button type="submit" className="bg-primary hover:bg-primary-hover" data-testid="filter-search">
                Cerca
              </Button>
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <X size={18} />
              </Button>
            </div>

            {/* Mobile Filters */}
            <div className="lg:hidden">
              <div className="flex items-center gap-2">
                <div className="flex-1 relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <Input
                    placeholder="Cerca per città..."
                    value={filters.city}
                    onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                    className="pl-10"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <SlidersHorizontal size={18} />
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary-hover">
                  Cerca
                </Button>
              </div>

              {showFilters && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <Select
                    value={filters.contract_type}
                    onValueChange={(value) => setFilters({ ...filters, contract_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo contratto" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {contractTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={filters.property_type}
                    onValueChange={(value) => setFilters({ ...filters, property_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo immobile" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      {propertyTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="Prezzo min"
                    value={filters.min_price}
                    onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                  />
                  <Input
                    type="number"
                    placeholder="Prezzo max"
                    value={filters.max_price}
                    onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                  />
                </div>
              )}
            </div>
          </form>
        </div>
      </section>

      {/* Properties Grid */}
      <section className="py-16 bg-background" data-testid="properties-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="font-sans text-text-muted">
              {loading ? 'Caricamento...' : `${properties.length} immobili trovati`}
            </p>
            <div className="flex items-center gap-2">
              <span className="font-sans text-sm text-text-muted whitespace-nowrap">Ordina per:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[200px]" data-testid="properties-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="price_asc" data-testid="sort-price-asc">Prezzo: dal più basso</SelectItem>
                  <SelectItem value="price_desc" data-testid="sort-price-desc">Prezzo: dal più alto</SelectItem>
                  <SelectItem value="recent" data-testid="sort-recent">Più recenti</SelectItem>
                  <SelectItem value="featured" data-testid="sort-featured">In evidenza</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-xl aspect-[4/3] mb-4" />
                  <div className="h-6 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-5 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-4 bg-gray-200 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : properties.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {properties.map((property, index) => (
                <PropertyCard key={property.id} property={property} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="font-sans text-text-muted text-lg">
                Nessun immobile trovato con i filtri selezionati.
              </p>
              <Button variant="link" onClick={clearFilters} className="mt-4 text-primary">
                Cancella filtri
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
